import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import * as spriteIcons from 'assets/svg/sprite'
import { IconPlusInBox } from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getMediaCategories } from 'services/endpoints/mediaCategory'
import { getProfile } from 'services/endpoints/profile'
// import { useMediaCategoryStore } from 'store/mediaCategoryStore'
import { useProfileCacheStore } from 'store/profileCacheStore'

import { AddCategoryModal } from './components/AddCategoryModal'
import { DeleteCategoryModal } from './components/DeleteCategoryModal'
import { EditCategoryModal } from './components/EditCategoryModal'

import './_media-categories.scss'

type MediaCategorySortType =
    | 'name'
    | 'date_created'
    | 'posts'
    | 'premium'

type MediaCategorySortOrder = 'asc' | 'desc'


export const MediaCategories = () => {
    const { setTitle } = usePageTitle()
    const profileCache = useProfileCacheStore()
    const cachedProfile = Object.values(profileCache.cache)[0]
    const username = cachedProfile?.username
    // const shouldFetch = !cachedProfile
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [existingNamesArray, setExistsingNamesArray] = useState<string[]>([])
    const [selectedCategory, setSelectedCategory] = useState<{
    name: string
    id: string | null
    public: number | null
    }>({
    name: '',
    id: null,
    public: null
    })

    
    const [sortOptions, setSortOptions] = useState<{
        type: MediaCategorySortType
        asc_or_desc: MediaCategorySortOrder
    }>({
        type: 'date_created',
        asc_or_desc: 'desc'
    })

    interface MediaCategory {
    id: string
    name: string
    public: number
    premium: number
    created_at?: string
    posts?: number
    messages?: number
    stories?: number
    selected: boolean
    suggestions?: boolean
    }
    
    const {
        data,
        isLoading,
        isFetching
    } = useQuery<{ data: MediaCategory[] }>(
        [
            'allMediaCategories',
            debouncedSearchTerm,
            sortOptions.type,
            sortOptions.asc_or_desc
        ],
        async () =>
            await getMediaCategories({
                search: debouncedSearchTerm || undefined,
                sort_by: sortOptions.type,
                sort_order: sortOptions.asc_or_desc
            }),
        {
            keepPreviousData: true,
            staleTime: 30_000,
            onSuccess: response => {
                const names = (response.data ?? []).map(
                    category => category.name
                )

                setExistsingNamesArray(names)
            }
        }
    )

    const modalData = useModalContext()

    const openAddCategoryModal = () => {
        modalData.addModal(
            t('addNewCategory'),
            <AddCategoryModal existingNamesArray={existingNamesArray} />,
            false,
            false,
            '',
            '',
            true // keyboardAdjust
        )
    }

    const location = useLocation()
    const navigate = useNavigate()
    const { t } = useTranslation()


    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim())
        }, 300)

        return () => {
            window.clearTimeout(timeout)
        }
    }, [searchTerm])

    useEffect(() => {
        const fetchProfileIfNeeded = async () => {
          if (!cachedProfile) {
            try {
              const res = await getProfile()
              if (res?.data) {
                profileCache.setProfile(username, res.data)
              }
            } catch (err) {
              console.error('Failed to fetch profile:', err)
            }
          }
        }
      
        fetchProfileIfNeeded()
      }, [username])

    useEffect(() => {
        setTitle(t('mediaCategories'))
    }, [setTitle])


    useEffect(() => {
        if (location?.state?.openAddModal) {
            openAddCategoryModal()
            navigate('/media_categories', { replace: true })
        }
    }, [location])

    return (
        <div className='media-categories-container'>
            <WithHeaderSection
                headerSection={
                    <LayoutHeader
                        title={t('mediaCategories')}
                        type='search-with-buttons'
                        searchValue={searchTerm}
                        searchFn={(val: string) => {
                            setSearchTerm(val)
                        }}
                        clearFn={() => {
                            setSearchTerm('')
                        }}
                        sortingProps={{
                            selectedSort: sortOptions.type,
                            selectedOrder: sortOptions.asc_or_desc
                        }}
                        additionalProps={{ placeholder: t('searchCategories') }}
                        buttons={[
                            {
                                type: 'sort',
                                defaultOrderBySort: {
                                    date_created: 'desc',
                                    name: 'asc',
                                    posts: 'desc',
                                    premium: 'desc'
                                },
                                elements: {
                                    first_section: [
                                        {
                                            value: 'date_created',
                                            name: t('dateCreated')
                                        },
                                        {
                                            value: 'name',
                                            name: t('name')
                                        },
                                        {
                                            value: 'posts',
                                            name: t('postQuantity')
                                        },
                                        {
                                            value: 'premium',
                                            name: t('premiumPostQuantity')
                                        }
                                    ],
                                    second_section: [
                                        {
                                            value: 'asc',
                                            name: t('ascending')
                                        },
                                        {
                                            value: 'desc',
                                            name: t('descending')
                                        }
                                    ]
                                }
                            },
                            {
                                type: 'cta',
                                icon: <spriteIcons.IconPlus />,
                                color: 'white',
                                action: openAddCategoryModal,
                                customClass: 'media-categories-add-button'
                            }
                        ]}
                        applyFn={(value: string, order: string) => {
                            setSortOptions({
                                type: value as MediaCategorySortType,
                                asc_or_desc: order as MediaCategorySortOrder
                            })
                        }}
                    />
                }
            >
                    {isLoading && (
                        <div className='media-categories-loader-container'>
                            <BeatLoader size={10} />
                        </div>
                    )}

                    {!isLoading && data?.data?.length === 0 && (
                        <div className='media-categories-empty-results'>
                            {t('noCategoriesFound')}
                        </div>
                    )}

                    {!isLoading &&
                        data?.data?.map(category => (
                        <ActionCard
                            key={category.id}
                            icon={
                                <div className='media-categories-category-icon-background'>
                                    <spriteIcons.IconHamburgerLinesWhite />
                                </div>
                            }
                            iconSize='small'
                            hasArrow
                            clickFn={() => {
                                navigate('/vault', {
                                    state: { categoryId: category.id }
                                  })
                            }}
                            text={
                                <span className='media-categories-name-container'>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                    <span className='media-categories-name'>
                                        {category.name}
                                    </span>
                                    {category.id !== null &&
                                     (category.public === 1 ? (
                                        <span
                                            style={{
                                                color: 'rgb(20, 206, 150)',
                                                border: '1px solid rgb(20, 206, 150)',
                                                borderRadius: '5px',
                                                padding: '2px 5px',
                                                fontSize:'1rem'
                                            }}
                                        >
                                            Public
                                        </span>
                                    ) : (
                                        <span
                                            style={{
                                                color: 'rgb(20, 206, 150)',
                                                border: '1px solid rgb(20, 206, 150)',
                                                borderRadius: '5px',
                                                padding: '2px 5px',
                                                fontSize:'1rem'
                                            }}
                                        >
                                            Private
                                        </span>
                                    ))}
                                    </div>
                                        <span className='media-categories-info'>
                                            {(category.posts ?? 0) > 0 && (
                                                <span className='media-categories-info-button'>
                                                    {category.posts ?? 0}{' '}
                                                    <span>{t('posts')}</span>
                                                </span>
                                            )}

                                            {(category.premium ?? 0) > 0 && (
                                                <span className='media-categories-info-button'>
                                                    {category.premium ?? 0}{' '}
                                                    <span>{t('Premium')}</span>
                                                </span>
                                            )}

                                            {(category.stories ?? 0) > 0 && (
                                                <span className='media-categories-info-button'>
                                                    {category.stories ?? 0}{' '}
                                                    <span>{t('stories')}</span>
                                                </span>
                                            )}

                                            {(category.messages ?? 0) > 0 && (
                                                <span className='media-categories-info-button'>
                                                    {category.messages ?? 0}{' '}
                                                    <span>{t('messages')}</span>
                                                </span>
                                            )}
                                        </span>
                                </span>
                            }
                            customHtml={
                                <span
                                    className={`media-categories-drawer-button ${
                                        selectedCategory.id === category.id &&
                                        'media-categories-drawer-button-open'
                                    }`}
                                    onClick={e => {
                                        e.stopPropagation()
                                        setDrawerOpen(true)
                                        setSelectedCategory({
                                            name: category.name,
                                            id: category.id,
                                            public: category.public,
                                        })
                                    }}
                                >
                                    <spriteIcons.IconThreeDots color='#767676' />
                                </span>
                            }
                        />
                    ))}

{!isLoading && data?.data?.length ? (
    <ActionCard
        icon={<IconPlusInBox width='28' height='28' />}
        iconSize='small'
        clickFn={openAddCategoryModal}
        description={
            <span className='media-categories-add-text'>
                {t('addNewCategory')}
            </span>
        }
    />
) : null}

                <div
                    className={`media-categories-drawer-overlay ${
                        drawerOpen ? 'media-categories-drawer-overlay-open' : ''
                    }`}
                >
                <div
                    className='media-categories-drawer-overlay-background'
                    onClick={() => {
                    setDrawerOpen(false)
                    setSelectedCategory({ name: '', id: null, public: null })
                    }}
                />
                <div className="media-categories-drawer">
                    <div
                    className="media-categories-drawer-option"
                    onClick={() => {
                        setDrawerOpen(false)
                        setSelectedCategory({ name: '', id: null, public: null })
                        modalData.addModal(
                            t('editCategory'),
                            <EditCategoryModal
                                selectedCategory={selectedCategory}
                                closeDrawer={() => {
                                    setDrawerOpen(false)
                                    setSelectedCategory({ name: '', id: null, public: null })
                                }}
                            />,
                            false,
                            false,
                            '',
                            '',
                            true // keyboardAdjust
                        )
                    }}
                    >
                    <spriteIcons.IconEdit />
                    {t('renameCategory')}
                    </div>

                    <div
                    className="media-categories-drawer-option"
                    onClick={() => {
                        modalData.addModal(
                        t('delete'),
                        <DeleteCategoryModal
                            selectedCategory={selectedCategory}
                            closeDrawer={() => {
                            setDrawerOpen(false)
                            setSelectedCategory({ name: '', id: null, public: null })
                            }}
                        />
                        )
                    }}
                    >
                    <spriteIcons.IconTrashcan width="19" height="19" />
                    {t('delete')}
                    </div>
                </div>
                </div>
            </WithHeaderSection>
            <DesktopAdditionalContent />
        </div>
    )
}
